package com.example.mystybag.login.views

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.extensions.keyboardPadding
import com.example.mystybag.common.model.RadioButtonOption
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.custom.RadioButtonGroup
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberUseNativeStyleWebLayout
import com.example.mystybag.login.logic.MerchantRegistrationPaymentDataViewModel
import com.example.mystybag.login.views.components.MerchantRegistrationProgressBar
import com.example.mystybag.login.views.components.MerchantRegistrationProgressFractions
import com.example.mystybag.login.views.views.MerchantRegistrationPaymentDataWebView
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.input.KeyboardType
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p36
import com.example.mystybag.core.ui.theme.LocalTheme.p8

@Composable
fun MerchantRegistrationPaymentDataView(viewModel: MerchantRegistrationPaymentDataViewModel) {
    if (PlatformHelper.isWeb) {
        MerchantRegistrationPaymentDataWebView(viewModel)
        return
    }
    UIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        horizontalAlignment = Alignment.Start
    ) {
        MerchantRegistrationPaymentDataForm(viewModel)
    }
}

@Composable
internal fun MerchantRegistrationPaymentDataForm(
    viewModel: MerchantRegistrationPaymentDataViewModel
) {
    val pinContinueToBottom = rememberUseNativeStyleWebLayout()
    val scrollState = rememberScrollState()
    if (pinContinueToBottom) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .fillMaxWidth()
                .keyboardPadding()
                .padding(bottom = p8)
        ) {
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .fillMaxHeight()
                    .verticalScroll(scrollState),
                horizontalAlignment = Alignment.Start,
                verticalArrangement = Arrangement.spacedBy(p0)
            ) {
                MerchantRegistrationPaymentDataScrollableBody(viewModel)
            }
            MerchantRegistrationPaymentDataFooter(viewModel)
        }
    } else {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(scrollState)
                .keyboardPadding()
                .padding(bottom = p8),
            horizontalAlignment = Alignment.Start,
            verticalArrangement = Arrangement.spacedBy(p0)
        ) {
            MerchantRegistrationPaymentDataScrollableBody(viewModel)
            MerchantRegistrationPaymentDataFooter(viewModel)
        }
    }
}

@Composable
internal fun MerchantRegistrationPaymentDataScrollableBody(
    viewModel: MerchantRegistrationPaymentDataViewModel
) {
    val state by viewModel.uiState.subscribeAsState()
    MerchantRegistrationProgressBar(MerchantRegistrationProgressFractions.PAYMENT_DATA)

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = p16)
    ) {
        Spacer(modifier = Modifier.height(p36))

        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(p24),
            horizontalAlignment = Alignment.Start
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(p16),
                horizontalAlignment = Alignment.Start
            ) {
                Texts.DisplaySmall(
                    text = stringResource(SR.strings.payment_data),
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = TextAlign.Start
                )
                Texts.BodyLarge(
                    text = stringResource(SR.strings.payment_data_description),
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = TextAlign.Start
                )
            }

            DefaultCircleShapedTextField(
                value = state.iban,
                onValueChange = viewModel::updateIban,
                label = stringResource(SR.strings.iban),
                modifier = Modifier.fillMaxWidth(),
                isError = state.ibanError != null,
                errorText = when (state.ibanError) {
                    "invalid_iban" -> stringResource(SR.strings.invalid_iban)
                    else -> ""
                },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text)
            )

            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(p16),
                horizontalAlignment = Alignment.Start
            ) {
                Texts.BodyLarge(
                    text = stringResource(SR.strings.payment_provider_title),
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = TextAlign.Start
                )
                RadioButtonGroup(
                    modifier = Modifier.fillMaxWidth(),
                    options = listOf(
                        RadioButtonOption(
                            id = 0,
                            text = stringResource(SR.strings.payment_provider_mypos),
                            code = "mypos",
                            isSelected = state.paymentProvider != "stripe"
                        ),
                        RadioButtonOption(
                            id = 1,
                            text = stringResource(SR.strings.payment_provider_stripe),
                            code = "stripe",
                            isSelected = state.paymentProvider == "stripe"
                        )
                    ),
                    onItemSelected = { id ->
                        viewModel.updatePaymentProvider(if (id == 1) "stripe" else "mypos")
                    }
                )
            }
        }
    }
}

@Composable
internal fun MerchantRegistrationPaymentDataFooter(
    viewModel: MerchantRegistrationPaymentDataViewModel
) {
    val state by viewModel.uiState.subscribeAsState()
    Spacer(modifier = Modifier.height(p32))
    DefaultButton(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = p16, vertical = p8),
        type = DefaultButtonType.PRIMARY,
        caption = stringResource(SR.strings.continue_camel_case),
        callback = viewModel::onContinue,
        enabled = !state.isLoading
    )
}
